// src/pages/Vitrine.js - Site vitrine : point d'entrée public avant connexion/inscription
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const CONTACT_INFOS = {
  adresse: "Boulevard Saraounia, goudron Nyala, Niamey, Niger",
  telephones: ["+227 77 33 77 77", "+227 75 01 75 75"],
  email: "sales@icontinuum.net",
};

const FEATURES = [
  {
    icon: "bi-buildings",
    title: "Sites & agents",
    text: "Centralisez vos clients, vos sites et vos affectations d'agents en un seul tableau de bord.",
  },
  {
    icon: "bi-shield-check",
    title: "Rondes QR & NFC",
    text: "Vos contrôleurs scannent chaque point de contrôle sur le terrain : la preuve de passage est automatique.",
  },
  {
    icon: "bi-exclamation-triangle",
    title: "Incidents géolocalisés",
    text: "Un incident signalé depuis le terrain arrive avec photo et position GPS, en temps réel.",
  },
  {
    icon: "bi-list-task",
    title: "Tâches & validation",
    text: "L'agent envoie sa tâche en validation, le gestionnaire clôture : rien ne se ferme sans contrôle.",
  },
  {
    icon: "bi-calendar-week",
    title: "Planning jour/soir",
    text: "Organisez les créneaux de vos agents et contrôleurs, journée ou soirée, site par site.",
  },
  {
    icon: "bi-journal-text",
    title: "Main courante",
    text: "Chaque évènement de service est journalisé par l'agent, consultable à tout moment par le gestionnaire.",
  },
  {
    icon: "bi-file-earmark-bar-graph",
    title: "Rapports exportables",
    text: "Rondes, incidents, tâches, abonnements : exportez en PDF ou Excel, filtrés par période.",
  },
  {
    icon: "bi-bell",
    title: "Alertes en temps réel",
    text: "Notifications push sur mobile : nouvel incident, tâche à valider, abonnement bientôt expiré.",
  },
];

const HERO_SLIDES = [
  {
    titre: "La sécurité de vos sites, pilotée depuis un seul endroit.",
    text: "SecureControle centralise la gestion de votre agence de gardiennage : agents, sites, rondes, incidents, tâches et facturation, suivis en temps réel, sur ordinateur comme sur mobile.",
  },
  {
    titre: "Chaque ronde, prouvée en un scan.",
    text: "QR code ou tag NFC : vos contrôleurs valident leur passage sur site, horodaté et géolocalisé.",
  },
  {
    titre: "Un incident, une photo, une position.",
    text: "Dès le terrain, l'incident remonte avec photo et GPS, traité en temps réel par le gestionnaire.",
  },
  {
    titre: "Rien ne se clôture sans validation.",
    text: "Chaque tâche passe par un contrôle avant d'être considérée comme terminée.",
  },
];

const MOBILE_POINTS = [
  { icon: "bi-qr-code-scan", titre: "Scan des points de ronde", text: "Un geste suffit pour valider le passage sur chaque point de contrôle." },
  { icon: "bi-camera", titre: "Incident avec photo et GPS", text: "Photo et position capturées automatiquement, transmises en temps réel." },
  { icon: "bi-check2-square", titre: "Tâches et main courante", text: "Mises à jour en direct, consultables par le gestionnaire à tout moment." },
  { icon: "bi-calendar3", titre: "Planning personnel", text: "Consultez vos créneaux de service à tout moment, où que vous soyez." },
];

const ETAPES = [
  {
    numero: "1",
    titre: "Inscrivez votre société",
    text: "Créez votre compte gestionnaire, ajoutez le NIF/RCCM de votre agence et, si vous le souhaitez, votre logo.",
  },
  {
    numero: "2",
    titre: "14 jours d'essai, aucune carte requise",
    text: "Dès la validation de votre société, votre période d'essai démarre automatiquement pour découvrir la plateforme.",
  },
  {
    numero: "3",
    titre: "Continuez avec myNITA ou Amana",
    text: "À la fin de l'essai, choisissez votre formule et payez directement depuis l'application, en Franc CFA.",
  },
];

const FAQ = [
  {
    q: "Que se passe-t-il à la fin des 14 jours d'essai ?",
    r: "L'accès aux fonctionnalités de gestion est suspendu tant qu'aucun abonnement n'est actif. Vos données restent conservées : un paiement suffit pour tout réactiver immédiatement.",
  },
  {
    q: "Puis-je payer mon abonnement à l'avance ?",
    r: "Oui. Vous pouvez régler avant l'expiration de votre abonnement en cours : la nouvelle période s'ajoute simplement à la suite de celle déjà payée.",
  },
  {
    q: "Puis-je changer de formule en cours d'abonnement ?",
    r: "Vous pouvez passer à une formule supérieure à tout moment. Le passage à une formule inférieure n'est pas proposé, pour éviter toute perte de données déjà enregistrées (clients, sites, tags).",
  },
  {
    q: "Mes agents ont-ils besoin d'installer une application ?",
    r: "Oui, une application mobile dédiée permet à vos agents et contrôleurs de scanner les rondes, signaler un incident avec photo, gérer leurs tâches et consulter leur planning.",
  },
  {
    q: "Comment mes agents remontent-ils un incident ?",
    r: "Depuis l'application mobile, avec une photo et la position GPS capturées automatiquement, transmises instantanément à votre tableau de bord.",
  },
];

const Vitrine = () => {
  const navigate = useNavigate();
  const [faqOuverte, setFaqOuverte] = useState(null);
  const [contactForm, setContactForm] = useState({ nom: "", email: "", objet: "", message: "" });
  const [contactStatut, setContactStatut] = useState({ etat: "idle", message: "" }); // idle | envoi | succes | erreur
  const [menuMobileOuvert, setMenuMobileOuvert] = useState(false);

  const heroCarouselRef = useRef(null);
  const mobileCarouselRef = useRef(null);
  const revealRefs = useRef([]);

  // Un visiteur déjà connecté n'a rien à faire sur la vitrine : direction le tableau de bord.
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (token) {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate]);

  // Bootstrap (bootstrap.bundle.min.js) est chargé une fois globalement dans
  // index.html : son scanner data-bs-ride ne s'exécute qu'au chargement
  // initial de la page, avant que React n'ait rendu ce composant, donc les
  // carrousels ci-dessous ne démarreraient jamais tout seuls (même pattern
  // que les tooltips ailleurs dans l'app — voir MainCouranteList.js) : on
  // instancie explicitement chaque carrousel après le montage.
  useEffect(() => {
    const reduireAnimations = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const instances = [];
    [heroCarouselRef, mobileCarouselRef].forEach((ref) => {
      if (ref.current && window.bootstrap?.Carousel) {
        instances.push(
          new window.bootstrap.Carousel(ref.current, {
            interval: reduireAnimations ? false : 4500,
            ride: reduireAnimations ? false : "carousel",
            pause: "hover",
            wrap: true,
            touch: true,
          })
        );
      }
    });
    return () => instances.forEach((c) => c.dispose());
  }, []);

  // Petite animation d'apparition au scroll (la page était jugée trop
  // statique) : chaque section/carte marquée .vitrine-reveal passe de
  // "en attente" à "visible" dès qu'elle entre dans le viewport, une seule
  // fois. Rien ne bouge pour les visiteurs qui ont demandé moins d'animations.
  useEffect(() => {
    const elements = revealRefs.current.filter(Boolean);
    if (!elements.length) return;

    // Filet de sécurité : si on ne peut pas (ou ne doit pas) animer, rendre le
    // contenu visible immédiatement plutôt que de le laisser cacher pour de
    // bon (CSS de départ : opacity 0) — voir aussi le repli CSS
    // @media (prefers-reduced-motion) dans theme-refresh.css.
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches || !("IntersectionObserver" in window)) {
      elements.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const addRevealRef = (el) => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  };

  const handleContactChange = (e) => {
    setContactForm({ ...contactForm, [e.target.name]: e.target.value });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactStatut({ etat: "envoi", message: "Envoi en cours..." });
    try {
      const response = await axios.post(`${API_BASE_URL}/contact/envoyer.php`, contactForm);
      if (response.data?.success) {
        setContactStatut({ etat: "succes", message: response.data.message || "Votre message a bien été envoyé. Merci !" });
        setContactForm({ nom: "", email: "", objet: "", message: "" });
      } else {
        setContactStatut({ etat: "erreur", message: response.data?.message || "Une erreur est survenue." });
      }
    } catch (err) {
      setContactStatut({
        etat: "erreur",
        message: err.response?.data?.message || "Impossible d'envoyer le message. Réessayez plus tard.",
      });
    }
  };

  return (
    <div className="vitrine-page">
      {/* Barre de navigation */}
      <header className="vitrine-nav">
        <div className="vitrine-nav-inner">
          <button
            type="button"
            className="vitrine-brand vitrine-brand-link"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            aria-label="Retour en haut de la page"
          >
            <img src="/assets/img/secureLogo.png" alt="SecureControle" />
            <span>SecureControle</span>
          </button>

          <nav className="vitrine-nav-menu">
            <a href="#fonctionnalites">Fonctionnalités</a>
            <a href="#etapes">Mise en route</a>
            <a href="#mobile">Application mobile</a>
            <a href="#faq">FAQ</a>
            <a href="#contact">Contact</a>
          </nav>

          <div className="vitrine-nav-actions">
            <Link to="/login" className="vitrine-btn vitrine-btn-ghost">
              Se connecter
            </Link>
            <Link to="/register" className="vitrine-btn vitrine-btn-primary">
              Créer un compte
            </Link>
          </div>

          <button
            type="button"
            className="vitrine-nav-burger"
            onClick={() => setMenuMobileOuvert(!menuMobileOuvert)}
            aria-label="Ouvrir le menu"
            aria-expanded={menuMobileOuvert}
          >
            <i className={`bi ${menuMobileOuvert ? "bi-x-lg" : "bi-list"}`}></i>
          </button>
        </div>

        {menuMobileOuvert && (
          <div className="vitrine-nav-mobile-panel">
            <a href="#fonctionnalites" onClick={() => setMenuMobileOuvert(false)}>Fonctionnalités</a>
            <a href="#etapes" onClick={() => setMenuMobileOuvert(false)}>Mise en route</a>
            <a href="#mobile" onClick={() => setMenuMobileOuvert(false)}>Application mobile</a>
            <a href="#faq" onClick={() => setMenuMobileOuvert(false)}>FAQ</a>
            <a href="#contact" onClick={() => setMenuMobileOuvert(false)}>Contact</a>
            <div className="vitrine-nav-mobile-actions">
              <Link to="/login" className="vitrine-btn vitrine-btn-ghost">
                Se connecter
              </Link>
              <Link to="/register" className="vitrine-btn vitrine-btn-primary">
                Créer un compte
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="vitrine-hero">
        <div className="vitrine-hero-glow" />
        <div className="vitrine-hero-inner">
          <div className="vitrine-trial-badge">
            <i className="bi bi-stars"></i> 14 jours d'essai gratuit, sans engagement
          </div>

          {/* Tout le panneau bleu est le carrousel : seuls le badge d'essai,
              les boutons d'action et le rang de paiement restent fixes —
              rotation du titre/texte uniquement, pour ne jamais faire
              disparaître l'appel à l'action. */}
          <div id="heroCarousel" className="carousel slide vitrine-hero-carousel" ref={heroCarouselRef}>
            <div className="carousel-inner">
              {HERO_SLIDES.map((slide, index) => (
                <div className={`carousel-item ${index === 0 ? "active" : ""}`} key={slide.titre}>
                  <h1>{slide.titre}</h1>
                  <p>{slide.text}</p>
                </div>
              ))}
            </div>
            <button className="carousel-control-prev" type="button" data-bs-target="#heroCarousel" data-bs-slide="prev">
              <span className="carousel-control-prev-icon" aria-hidden="true"></span>
              <span className="visually-hidden">Précédent</span>
            </button>
            <button className="carousel-control-next" type="button" data-bs-target="#heroCarousel" data-bs-slide="next">
              <span className="carousel-control-next-icon" aria-hidden="true"></span>
              <span className="visually-hidden">Suivant</span>
            </button>
            <div className="carousel-indicators vitrine-hero-indicators">
              {HERO_SLIDES.map((slide, index) => (
                <button
                  key={slide.titre}
                  type="button"
                  data-bs-target="#heroCarousel"
                  data-bs-slide-to={index}
                  className={index === 0 ? "active" : ""}
                  aria-label={slide.titre}
                ></button>
              ))}
            </div>
          </div>

          <div className="vitrine-hero-actions">
            <Link to="/register" className="vitrine-btn vitrine-btn-accent vitrine-btn-lg">
              <i className="bi bi-rocket-takeoff"></i> Démarrer l'essai gratuit
            </Link>
            <Link to="/login" className="vitrine-btn vitrine-btn-outline vitrine-btn-lg">
              J'ai déjà un compte
            </Link>
          </div>

          <div className="vitrine-payment-row">
            <span>Paiement de l'abonnement accepté via</span>
            <div className="vitrine-payment-logos">
              <img src="/assets/img/mynita_logo.png" alt="myNITA" />
              <img src="/assets/img/amana_logo.png" alt="Amana" />
            </div>
          </div>
        </div>
      </section>

      {/* Fonctionnalités */}
      <section className="vitrine-section" id="fonctionnalites">
        <div className="vitrine-section-head vitrine-reveal" ref={addRevealRef}>
          <span className="vitrine-eyebrow">Fonctionnalités</span>
          <h2>Tout votre dispositif de gardiennage, dans une seule application</h2>
        </div>
        <div className="vitrine-features-grid">
          {FEATURES.map((f, index) => (
            <div
              className="vitrine-feature-card vitrine-reveal"
              key={f.title}
              ref={addRevealRef}
              style={{ transitionDelay: `${(index % 4) * 80}ms` }}
            >
              <div className="vitrine-feature-icon">
                <i className={`bi ${f.icon}`}></i>
              </div>
              <h3>{f.title}</h3>
              <p>{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="vitrine-section vitrine-section-alt" id="etapes">
        <div className="vitrine-section-head">
          <span className="vitrine-eyebrow">Mise en route</span>
          <h2>Opérationnel en trois étapes</h2>
        </div>
        <div className="vitrine-etapes">
          {ETAPES.map((e, index) => (
            <div
              className="vitrine-etape vitrine-reveal"
              key={e.numero}
              ref={addRevealRef}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="vitrine-etape-numero">{e.numero}</div>
              <h3>{e.titre}</h3>
              <p>{e.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Mobile */}
      <section className="vitrine-section vitrine-mobile-section" id="mobile">
        <div className="vitrine-mobile-text vitrine-reveal" ref={addRevealRef}>
          <span className="vitrine-eyebrow">Application mobile</span>
          <h2>Vos agents et contrôleurs, connectés depuis le terrain</h2>

          <div id="mobileCarousel" className="carousel slide vitrine-mobile-carousel" ref={mobileCarouselRef}>
            <div className="carousel-inner">
              {MOBILE_POINTS.map((p, index) => (
                <div className={`carousel-item ${index === 0 ? "active" : ""}`} key={p.titre}>
                  <div className="vitrine-mobile-point">
                    <i className={`bi ${p.icon}`}></i>
                    <div>
                      <strong>{p.titre}</strong>
                      <p>{p.text}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="carousel-indicators vitrine-mobile-indicators">
              {MOBILE_POINTS.map((p, index) => (
                <button
                  key={p.titre}
                  type="button"
                  data-bs-target="#mobileCarousel"
                  data-bs-slide-to={index}
                  className={index === 0 ? "active" : ""}
                  aria-label={p.titre}
                ></button>
              ))}
            </div>
          </div>
        </div>
        <div className="vitrine-mobile-visual vitrine-reveal" ref={addRevealRef}>
          <i className="bi bi-phone"></i>
        </div>
      </section>

      {/* FAQ */}
      <section className="vitrine-section" id="faq">
        <div className="vitrine-section-head">
          <span className="vitrine-eyebrow">Questions fréquentes</span>
          <h2>Ce qu'il faut savoir avant de commencer</h2>
        </div>
        <div className="vitrine-faq">
          {FAQ.map((item, index) => {
            const ouverte = faqOuverte === index;
            return (
              <div
                className={`vitrine-faq-item vitrine-reveal ${ouverte ? "is-open" : ""}`}
                key={item.q}
                ref={addRevealRef}
                style={{ transitionDelay: `${index * 60}ms` }}
              >
                <button
                  className="vitrine-faq-question"
                  onClick={() => setFaqOuverte(ouverte ? null : index)}
                >
                  <span>{item.q}</span>
                  <i className={`bi ${ouverte ? "bi-dash" : "bi-plus"}`}></i>
                </button>
                {ouverte && <div className="vitrine-faq-reponse">{item.r}</div>}
              </div>
            );
          })}
        </div>
      </section>

      {/* Contact */}
      <section className="vitrine-section vitrine-section-alt" id="contact">
        <div className="vitrine-section-head">
          <span className="vitrine-eyebrow">Contact</span>
          <h2>Une question ? Écrivez-nous</h2>
        </div>
        <div className="vitrine-contact">
          <div className="vitrine-contact-infos">
            <div className="vitrine-contact-item">
              <i className="bi bi-geo-alt"></i>
              <span>{CONTACT_INFOS.adresse}</span>
            </div>
            {CONTACT_INFOS.telephones.map((tel) => (
              <div className="vitrine-contact-item" key={tel}>
                <i className="bi bi-telephone"></i>
                <span>{tel}</span>
              </div>
            ))}
            <div className="vitrine-contact-item">
              <i className="bi bi-envelope"></i>
              <span>{CONTACT_INFOS.email}</span>
            </div>
          </div>

          <form className="vitrine-contact-form" onSubmit={handleContactSubmit}>
            <div className="vitrine-contact-row">
              <input
                type="text"
                name="nom"
                placeholder="Nom"
                value={contactForm.nom}
                onChange={handleContactChange}
                required
              />
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={contactForm.email}
                onChange={handleContactChange}
                required
              />
            </div>
            <input
              type="text"
              name="objet"
              placeholder="Objet"
              value={contactForm.objet}
              onChange={handleContactChange}
              required
            />
            <textarea
              name="message"
              placeholder="Message"
              rows={5}
              value={contactForm.message}
              onChange={handleContactChange}
              required
            />

            {contactStatut.etat !== "idle" && (
              <div className={`vitrine-contact-statut vitrine-contact-statut-${contactStatut.etat}`}>
                {contactStatut.message}
              </div>
            )}

            <button type="submit" className="vitrine-btn vitrine-btn-primary vitrine-btn-lg" disabled={contactStatut.etat === "envoi"}>
              {contactStatut.etat === "envoi" ? "Envoi en cours..." : "Envoyer le message"}
            </button>
          </form>
        </div>
      </section>

      {/* Appel à l'action final */}
      <section className="vitrine-cta">
        <h2>Prêt à professionnaliser le suivi de vos sites ?</h2>
        <p>Créez votre société en quelques minutes et profitez de 14 jours d'essai gratuit.</p>
        <Link to="/register" className="vitrine-btn vitrine-btn-accent vitrine-btn-lg">
          <i className="bi bi-rocket-takeoff"></i> Créer mon compte
        </Link>
      </section>

      {/* Pied de page */}
      <footer className="vitrine-footer">
        <div className="vitrine-footer-brand">
          <img src="/assets/img/secureLogo.png" alt="SecureControle" />
          <span>SecureControle</span>
        </div>
        <div className="vitrine-footer-links">
          <Link to="/login" className="vitrine-btn vitrine-btn-outline">
            Se connecter
          </Link>
          <Link to="/register" className="vitrine-btn vitrine-btn-accent">
            Créer un compte
          </Link>
        </div>
        <div className="vitrine-footer-bottom">
          <span>© {new Date().getFullYear()} SecureControle. Tous droits réservés.</span>
          <a href="https://icontinuum.net" target="_blank" rel="noreferrer">
            Propulsé par iContinuum
          </a>
        </div>
      </footer>
    </div>
  );
};

export default Vitrine;
